import { useObjectContext } from '@components/v2/layout/ObjectScreen/hooks/useObjectContext';
import EmptyState from '@components/v2/ui/EmptyState/EmptyState';
import Typography from '@components/v2/ui/Typography/Typography';
import { CircularProgress, Stack } from '@mui/material';
import LangEnum from '../../../../../../lang/LangEnum';
import { FilterPreferenceRecognizedLists } from '../../../../../../services/modules/userContextPreferences/userContextPreferences.types';
import { OrderObjectActivitiesTestIds } from '../../../../../../utils/testIds/OrderObject.testIds';
import ActivityFeedFilterBar from '../../../../../activity-feed/components/ActivityFeedFilterBar/ActivityFeedFilterBar';
import ActivityFeedList from '../../../../../activity-feed/components/ActivityFeedList/ActivityFeedList';
import { useTranslation } from '../../../../../translation/TranslationContext';
import useOrderObjectActivitiesTab from './hooks/useOrderObjectActivitiesTab';

interface OrderObjectActivitiesTabProps {
  orderId: string;
}

const OrderObjectActivitiesTab = ({ orderId }: OrderObjectActivitiesTabProps) => {
  const { translate } = useTranslation();
  const { scrollParent } = useObjectContext();
  const {
    domains,
    groups,
    groupCounts,
    isEmpty,
    isLoading,
    isError,
    isAccessDenied,
    isFetchingNextPage,
    fetchNextPage,
  } = useOrderObjectActivitiesTab({ orderId });

  const renderBody = () => {
    if (isLoading) {
      return (
        <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Stack>
      );
    }

    if (isError) {
      return (
        <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center', py: 6 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {translate(
              isAccessDenied
                ? LangEnum.SNACKBAR_ACCESS_DENIED
                : LangEnum.SNACKBAR_ERROR_OCCURRED_TRY_AGAIN,
            )}
          </Typography>
        </Stack>
      );
    }

    if (isEmpty) {
      return (
        <Stack sx={{ flex: '1 0 auto', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <EmptyState
            testId={OrderObjectActivitiesTestIds.EMPTY_STATE}
            title={{ text: LangEnum.ORDER_OBJECT_ACTIVITIES_EMPTY_TITLE }}
            description={{ text: LangEnum.ORDER_OBJECT_ACTIVITIES_EMPTY_DESCRIPTION }}
          />
        </Stack>
      );
    }

    return (
      <ActivityFeedList
        groups={groups}
        groupCounts={groupCounts}
        isLoadingMore={isFetchingNextPage}
        scrollParent={scrollParent}
        testId={OrderObjectActivitiesTestIds.LIST_CONTAINER}
        rowTestId={OrderObjectActivitiesTestIds.LIST_ITEM}
        rowSnapshotTestId={OrderObjectActivitiesTestIds.DOWNLOAD_SNAPSHOT_BUTTON}
        rowTrackingTestId={OrderObjectActivitiesTestIds.TRACK_SHIPMENT_BUTTON}
        onEndReached={() => void fetchNextPage()}
      />
    );
  };

  return (
    <>
      <ActivityFeedFilterBar
        list={FilterPreferenceRecognizedLists.ORDER_OBJECT_ACTIVITIES}
        domains={domains}
      />

      {renderBody()}
    </>
  );
};

export default OrderObjectActivitiesTab;
